import React, { useMemo, useState } from 'react';

import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { AlineChip } from 'src/components';
import { useGetUserLabels } from 'src/hooks';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';

export function LabelsFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const { labels } = useGetUserLabels();
  const [searchTerm, setSearchTerm] = useState('');
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const options = useMemo(
    () => labels.map(label => ({ id: label.id, label: label.labelText })),
    [labels]
  );

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map((v: { id: string }) => v.id) : []);
  }, [filterModel, field]);

  const handleToggle = (labelId: string) => {
    const label = labels.find(l => l.id === labelId);
    if (!label) return;

    const currentValues = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(labelId);

    const newValues = isSelected
      ? currentValues.filter((v: { id: string }) => v.id !== labelId)
      : [
          ...currentValues,
          { __typename: 'Label' as const, id: label.id, labelText: label.labelText },
        ];

    const updatedItems = filterModel.items.filter(item => item.field !== field);

    if (newValues.length > 0) {
      updatedItems.push({ field, operator, value: newValues });
    }

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => <AlineChip chipText={option.label} variant="label" />}
      searchPlaceholder="Search labels..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
